import { axios } from '@/api/common.ts'
import {
  BomDetailOptionModel,
  BomDetailOptionSchema,
  ItemDetailOptionModel,
  ItemDetailOptionSchema,
  ItemSelectOption,
  ItemSelectOptions,
  ItemSelectOptionsSchema,
} from '@/api/options/item.schema.ts'
import { queryOptions } from '@tanstack/react-query'

export const fetchItemSelectOptions = () =>
  queryOptions({
    queryKey: ['items', 'all'],
    queryFn: () => fetchItemAll(),
  })

export async function fetchItemAll(): Promise<ItemSelectOptions> {
  const response = await axios.get('/api/masterdata/options/item')
  return ItemSelectOptionsSchema.parse(response.data)
}

export async function fetchItemOptionById(
  id: number
): Promise<ItemDetailOptionModel> {
  const response = await axios.get(`/api/masterdata/options/item/${id}`)
  return ItemDetailOptionSchema.parse(response.data)
}

export const getItemOptionByIdQueryOptions = (id: number) =>
  queryOptions({
    queryKey: ['options', 'item', id],
    queryFn: () => fetchItemOptionById(id),
  })

export async function fetchItemBomOptionById(
  itemId: number,
  bomId: number
): Promise<BomDetailOptionModel> {
  const response = await axios.get(
    `/api/masterdata/options/item/${itemId}/bom/${bomId}`
  )
  return BomDetailOptionSchema.parse(response.data)
}

export const getItemBomOptionByIdQueryOptions = (
  itemId: number,
  bomId: number
) =>
  queryOptions({
    queryKey: ['options', 'bom', itemId, bomId],
    queryFn: () => fetchItemBomOptionById(itemId, bomId),
    enabled: itemId > 0 && bomId > 0,
  })

export type FilterOption = {
  canBePurchased?: boolean
  canBeManufactured?: boolean
  canBeUsedInBom?: boolean
  canBeSold?: boolean
  isMoldTracking?: boolean
  isLotTracking?: boolean
  itemType?: string
  category?: string
}
// utilities function for filtering items
export function filterItems(data: ItemSelectOption[], option: FilterOption) {
  return data.filter((item) => {
    let match = true
    if (option.canBePurchased !== undefined) {
      match = match && item.canBePurchased === option.canBePurchased
    }
    if (option.canBeManufactured !== undefined) {
      match = match && item.canBeManufactured === option.canBeManufactured
    }
    if (option.canBeUsedInBom !== undefined) {
      match = match && item.canBeUsedInBom === option.canBeUsedInBom
    }
    if (option.canBeSold !== undefined) {
      match = match && item.canBeSold === option.canBeSold
    }
    if (option.isMoldTracking !== undefined) {
      match = match && item.isMoldTracking === option.isMoldTracking
    }
    if (option.isLotTracking !== undefined) {
      match = match && item.isLotTracking === option.isLotTracking
    }
    if (option.itemType !== undefined) {
      match = match && item.type === option.itemType
    }
    if (option.category !== undefined) {
      match = match && item.category === option.category
    }

    return match
  })
}

export const CanBePurchaseOption: FilterOption = {
  canBePurchased: true,
}

export const CanBeManufactureOption: FilterOption = {
  canBeManufactured: true,
}

export const IsFGCategoryOption: FilterOption = {
  category: 'FG',
}

export const IsRMCategoryOption: FilterOption = {
  category: 'RM',
}

export const FGTypeOptions = [
  {
    label: 'MP',
    value: 'MP',
  },
  {
    label: 'HD',
    value: 'HD',
  },
  {
    label: 'Trade Good',
    value: 'TG',
  },
]

export const RMTypeOptions = [
  {
    label: 'Head',
    value: 'head',
  },
  {
    label: 'Cap',
    value: 'cap',
  },
  {
    label: 'Chaplet',
    value: 'chaplet',
  },
  {
    label: 'Housing',
    value: 'housing',
  },
  {
    label: 'Piston',
    value: 'piston',
  },
  {
    label: 'Shaft',
    value: 'shaft',
  },
  {
    label: 'Stem',
    value: 'stem',
  },
  {
    label: 'Gasket',
    value: 'gasket',
  },
  {
    label: 'Ball',
    value: 'ball',
  },
  {
    label: 'Spring',
    value: 'spring',
  },
  {
    label: 'Tube',
    value: 'tube',
  },
  {
    label: 'Aluminium Collar',
    value: 'aluminium collar',
  },
]

export function getFGTypeLabel(value: string) {
  const option = FGTypeOptions.find((item) => item.value === value)
  return option?.label
}

export function getRMTypeLabel(value: string) {
  const option = RMTypeOptions.find((item) => item.value === value)
  return option?.label
}

export function getTypeLabel(group: string | null, value: string | null) {
  if (!group) {
    return value
  }
  if (group === 'FG') {
    return value ? getFGTypeLabel(value) : ''
  } else {
    return value ? getRMTypeLabel(value) : ''
  }
}
